import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsApi, Job } from '../../lib/api';
import { useRBAC } from '../../contexts/RBACContext';
import { useToast } from '../../components/ToastHost';
import { useConfirm } from '../../components/ConfirmHost';
// Phase 2 design system
import {
  AuroraPage, PageHeader, Card, Button, Icon, Badge, EmptyState, Input, Select,
} from '../../components/ui';
import type { BadgeTone } from '../../components/ui';

const PRIORITY_TONE: Record<Job['priority'], BadgeTone> = {
  urgent: 'danger',
  high: 'warn',
  normal: 'neutral',
  low: 'outline',
};

const STATUS_TONE: Record<Job['status'], BadgeTone> = {
  draft: 'neutral',
  open: 'good',
  on_hold: 'warn',
  filled: 'info',
  closed: 'neutral',
  cancelled: 'danger',
};

const selectStyle: React.CSSProperties = { width: 'auto', minWidth: 150, flex: '0 0 auto' };

// An Open job with no location AND no pay of any kind is withheld from the
// public careers board, embed widget, and syndication feeds (same rule as the
// backend). Surface that so it's clear why an "Open" job isn't public — usually
// it just needs a city/state or a pay rate filled in.
function isOffPublicBoard(j: Job): boolean {
  const hasLocation = !!(j.city?.trim() || j.state?.trim());
  const hasPay = (j.pay_rate_min ?? 0) > 0 || (j.pay_rate_max ?? 0) > 0 || (j.pay_rate ?? 0) > 0;
  return j.status === 'open' && !hasLocation && !hasPay;
}

// Compact table cells — thin rows so more jobs fit on screen.
const th: React.CSSProperties = {
  textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
  textTransform: 'uppercase', color: 'var(--t3)', padding: '8px 12px', whiteSpace: 'nowrap',
};
const thNum: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '8px 12px', fontSize: 13, color: 'var(--t2)', verticalAlign: 'middle' };
const tdNum: React.CSSProperties = { ...td, textAlign: 'right', fontWeight: 600, color: 'var(--t1)', fontVariantNumeric: 'tabular-nums' };

export default function JobList() {
  const nav = useNavigate();
  const { can } = useRBAC();
  const toast = useToast();
  const confirm = useConfirm();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('open');
  const [priority, setPriority] = useState<string>('');
  const [profession, setProfession] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const canDelete = can('candidates_delete');
  const canEdit = can('candidates_edit');
  const canCreate = can('candidates_create');
  const canBulk = canDelete || canEdit || canCreate;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (profession) params.profession = profession;
      if (search) params.search = search;
      const res = await jobsApi.list(params);
      setJobs(res.data.jobs);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, priority, profession]);

  // Drop any selections that no longer exist after a reload/filter change.
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(jobs.map((j) => j.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [jobs]);

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    load();
  };

  // Sort once per data/sort change rather than on every render.
  const sortedJobs = useMemo(
    () => [...jobs].sort((a, b) => {
      const ta = new Date(a.created_at ?? 0).getTime();
      const tb = new Date(b.created_at ?? 0).getTime();
      return sort === 'newest' ? tb - ta : ta - tb;
    }),
    [jobs, sort],
  );

  // Flag duplicate OPEN postings — a job whose title + client + facility +
  // city + state + profession + specialty matches another open job. Mirrors the
  // "Remove duplicates" backend grouping so what's flagged is what a dedupe
  // would clear. One per group is kept (the copy with the most applicants,
  // oldest as the tie-break); every other copy is flagged so the repeats are
  // visible in the list, not just removable. Templates (TMPL-*) are excluded.
  const duplicateIds = useMemo(() => {
    const norm = (s?: string | null) => (s ?? '').trim().toLowerCase();
    const keyOf = (j: Job) => [
      norm(j.title), j.client_id ?? '', j.facility_id ?? '',
      norm(j.city), norm(j.state), norm(j.profession), norm(j.specialty),
    ].join('|');
    const groups = new Map<string, Job[]>();
    for (const j of jobs) {
      if (j.status !== 'open') continue;
      if ((j.job_code ?? '').startsWith('TMPL-')) continue;
      const k = keyOf(j);
      const list = groups.get(k);
      if (list) list.push(j); else groups.set(k, [j]);
    }
    const dupes = new Set<string>();
    for (const arr of groups.values()) {
      if (arr.length < 2) continue;
      const ranked = [...arr].sort((a, b) =>
        (b.submission_count ?? 0) - (a.submission_count ?? 0) ||
        new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime()
      );
      for (let i = 1; i < ranked.length; i++) dupes.add(ranked[i].id);
    }
    return dupes;
  }, [jobs]);

  const allSelected = sortedJobs.length > 0 && selected.size === sortedJobs.length;
  const someSelected = selected.size > 0 && !allSelected;
  const headerRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === sortedJobs.length ? new Set() : new Set(sortedJobs.map((j) => j.id))));
  };
  const clearSelection = () => setSelected(new Set());

  // Run a bulk action across the selected jobs, tolerating partial failure.
  const runBulk = async (fn: (id: string) => Promise<unknown>, verb: string) => {
    const ids = [...selected];
    if (!ids.length) return;
    setBusy(true);
    const results = await Promise.allSettled(ids.map(fn));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    setBusy(false);
    clearSelection();
    await load();
    if (fail === 0) toast.success(`${verb} ${ok} job${ok === 1 ? '' : 's'}.`);
    else toast.warning(`${verb} ${ok}, ${fail} failed.`);
  };

  const bulkDelete = async () => {
    const n = selected.size;
    const ok = await confirm({
      title: `Delete ${n} job${n === 1 ? '' : 's'}?`,
      description: 'This permanently removes the selected jobs. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    void runBulk((id) => jobsApi.delete(id), 'Deleted');
  };
  // Clear out stub/test jobs: Open records with no location and no pay. Previews
  // first (and never touches a job that has applicants), then archives to draft.
  const [cleaning, setCleaning] = useState(false);
  const cleanupIncomplete = async () => {
    setCleaning(true);
    try {
      const { data } = await jobsApi.incompletePreview();
      if (data.archivable === 0) {
        toast.success(data.total > 0
          ? 'The only incomplete jobs have applicants, so they were left open.'
          : 'No incomplete jobs to clean up — your list is tidy.');
        return;
      }
      // List only the jobs that will actually be archived (those without
      // applicants), so the preview matches the "Archive N" count.
      const toArchive = data.jobs.filter((j) => j.submission_count === 0);
      const sample = toArchive.slice(0, 8).map((j) => `• ${j.title || '(untitled)'}`).join('\n');
      const more = toArchive.length > 8 ? `\n…and ${toArchive.length - 8} more` : '';
      const skipNote = data.with_applicants > 0
        ? `\n\n${data.with_applicants} job${data.with_applicants === 1 ? ' has' : 's have'} applicants and will be left open.`
        : '';
      const ok = await confirm({
        title: `Archive ${data.archivable} incomplete job${data.archivable === 1 ? '' : 's'}?`,
        description: `These are Open jobs with no location and no pay — usually test or half-finished records. They'll be set to Draft (removed from the public board and the open list; reversible).${skipNote}\n\n${sample}${more}`,
        confirmLabel: `Archive ${data.archivable}`,
      });
      if (!ok) return;
      const res = await jobsApi.cleanupIncomplete();
      toast.success(`Archived ${res.data.archived} incomplete job${res.data.archived === 1 ? '' : 's'}.`);
      await load();
    } catch {
      toast.error('Could not clean up incomplete jobs.');
    } finally {
      setCleaning(false);
    }
  };

  // Remove duplicate postings: OPEN jobs that repeat another open job (same
  // title, client, location). Previews first (and never touches a copy that has
  // applicants), keeps one per group, and archives the extras to Draft.
  const [deduping, setDeduping] = useState(false);
  const removeDuplicates = async () => {
    setDeduping(true);
    try {
      const { data } = await jobsApi.duplicatesPreview();
      if (data.archivable === 0) {
        toast.success(data.duplicate_records > 0
          ? 'The only duplicates have applicants, so they were left open.'
          : 'No duplicate jobs found — your list is tidy.');
        return;
      }
      const shown = Math.min(8, data.sample.length);
      const sample = data.sample.slice(0, shown)
        .map((j) => `• ${j.title || '(untitled)'}${j.city ? ` — ${j.city}${j.state ? `, ${j.state}` : ''}` : ''}`)
        .join('\n');
      const more = data.archivable > shown ? `\n…and ${data.archivable - shown} more` : '';
      const skipNote = data.kept_with_applicants > 0
        ? `\n\n${data.kept_with_applicants} duplicate${data.kept_with_applicants === 1 ? ' has' : 's have'} applicants and will be left open.`
        : '';
      const ok = await confirm({
        title: `Archive ${data.archivable} duplicate job${data.archivable === 1 ? '' : 's'}?`,
        description: `These are Open jobs that repeat another open posting (same title, client, and location). The original — or the copy that already has applicants — is kept; the extras are set to Draft (removed from the public board and the open list; reversible).${skipNote}\n\n${sample}${more}`,
        confirmLabel: `Archive ${data.archivable}`,
      });
      if (!ok) return;
      const res = await jobsApi.dedupe();
      toast.success(`Archived ${res.data.archived} duplicate job${res.data.archived === 1 ? '' : 's'}.`);
      await load();
    } catch {
      toast.error('Could not remove duplicate jobs.');
    } finally {
      setDeduping(false);
    }
  };

  const bulkDuplicate = () => void runBulk((id) => jobsApi.duplicate(id), 'Duplicated');
  const bulkStatus = (newStatus: string) => {
    if (!newStatus) return;
    void runBulk((id) => jobsApi.update(id, { status: newStatus as Job['status'] }), 'Updated');
  };

  return (
    <AuroraPage style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader
        icon={<Icon name="jobs" size={22} />}
        title="Jobs"
        subtitle={loading
          ? 'Loading…'
          : `${jobs.length} job${jobs.length === 1 ? '' : 's'}` +
            (duplicateIds.size > 0 ? ` · ${duplicateIds.size} duplicate${duplicateIds.size === 1 ? '' : 's'} flagged` : '')}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {canDelete && (
              <Button variant="secondary" icon={<Icon name="trash" size={14} />} loading={cleaning} onClick={() => void cleanupIncomplete()}>
                Clean up incomplete
              </Button>
            )}
            {canDelete && (
              <Button variant="secondary" icon={<Icon name="refresh" size={14} />} loading={deduping} onClick={() => void removeDuplicates()}>
                Remove duplicates
              </Button>
            )}
            {canCreate && (
              <Button icon={<Icon name="plus" size={15} />} onClick={() => nav('/jobs/new')}>
                New Job
              </Button>
            )}
          </div>
        }
      />

      {/* Filters */}
      <Card padded style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <form onSubmit={onSearchSubmit} style={{ display: 'flex', gap: 6, flex: '1 1 240px', minWidth: 240 }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, code, city, state…"
              style={{ flex: 1 }}
            />
            <Button type="submit" variant="secondary" icon={<Icon name="search" size={15} />}>
              Search
            </Button>
          </form>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} style={selectStyle} aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="open">Open</option>
            <option value="on_hold">On hold</option>
            <option value="filled">Filled</option>
            <option value="closed">Closed</option>
          </Select>
          <Select value={priority} onChange={(e) => setPriority(e.target.value)} style={selectStyle} aria-label="Filter by priority">
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </Select>
          <Select value={profession} onChange={(e) => setProfession(e.target.value)} style={selectStyle} aria-label="Filter by profession">
            <option value="">All professions</option>
            <option value="RN">RN</option>
            <option value="LPN">LPN</option>
            <option value="LVN">LVN</option>
            <option value="CNA">CNA</option>
            <option value="RT">RT</option>
            <option value="NP">NP</option>
            <option value="PA">PA</option>
            <option value="Other">Other</option>
          </Select>
          <Select value={sort} onChange={(e) => setSort(e.target.value as 'newest' | 'oldest')} style={selectStyle} title="Sort by date posted" aria-label="Sort">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </Select>
        </div>
      </Card>

      {error && (
        <div style={{ padding: 12, background: 'var(--danger-tint)', color: 'var(--danger-fg)', borderRadius: 'var(--r-md)', marginBottom: 12 }}>{error}</div>
      )}

      {/* Bulk action bar — shows when jobs are selected */}
      {canBulk && selected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: 12,
          background: 'var(--surface-elevated)', border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-md)', position: 'sticky', top: 8, zIndex: 5,
        }}>
          <span style={{ fontWeight: 600, color: 'var(--t1)', fontSize: 13 }}>
            {selected.size} selected
          </span>
          <div style={{ flex: 1 }} />
          {canEdit && (
            <Select
              value=""
              onChange={(e) => bulkStatus(e.target.value)}
              disabled={busy}
              style={{ width: 'auto', minWidth: 150 }}
              aria-label="Change status of selected"
            >
              <option value="">Change status…</option>
              <option value="open">Open</option>
              <option value="on_hold">On hold</option>
              <option value="filled">Filled</option>
              <option value="closed">Closed</option>
              <option value="draft">Draft</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          )}
          {canCreate && (
            <Button variant="secondary" onClick={bulkDuplicate} disabled={busy} icon={<Icon name="clipboard" size={14} />}>
              Duplicate
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={bulkDelete} disabled={busy} icon={<Icon name="trash" size={14} />}>
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={clearSelection} disabled={busy}>Clear</Button>
        </div>
      )}

      {/* Job table — dense rows */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)' }}>Loading jobs…</div>
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={<Icon name="jobs" size={26} />}
          title="No jobs found"
          description={canCreate ? 'Create a job to start filling a new position.' : 'Try adjusting your filters to see more results.'}
          action={canCreate ? <Button icon={<Icon name="plus" size={15} />} onClick={() => nav('/jobs/new')}>New Job</Button> : undefined}
        />
      ) : (
        <Card padded={false} style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface-subtle)', borderBottom: '1px solid var(--border)' }}>
                  {canBulk && (
                    <th style={{ ...th, width: 36, paddingRight: 0 }}>
                      <input
                        ref={headerRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        aria-label="Select all jobs"
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                  )}
                  <th style={th}>Job</th>
                  <th style={th}>Details</th>
                  <th style={thNum}>Subs</th>
                  <th style={thNum}>Pos</th>
                  <th style={thNum}>Age</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.map((j) => {
                  const isSel = selected.has(j.id);
                  return (
                    <tr
                      key={j.id}
                      onClick={() => nav(`/jobs/${j.id}`)}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: isSel ? 'var(--surface-subtle)' : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = 'var(--surface-subtle)'; }}
                      onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {canBulk && (
                        <td style={{ ...td, width: 36, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleOne(j.id)}
                            aria-label={`Select ${j.title}`}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                      )}
                      <td style={{ ...td, maxWidth: 360 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                          <span style={{ fontWeight: 600, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {j.title}
                          </span>
                          {j.job_code && (
                            <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: 'var(--font-mono)', flex: '0 0 auto' }}>{j.job_code}</span>
                          )}
                          {duplicateIds.has(j.id) && (
                            <Badge tone="warn" style={{ flex: '0 0 auto' }} title="Repeats another open job posting">Duplicate</Badge>
                          )}
                          <Badge tone={PRIORITY_TONE[j.priority]} style={{ textTransform: 'uppercase', flex: '0 0 auto' }}>{j.priority}</Badge>
                        </div>
                      </td>
                      <td style={{ ...td, color: 'var(--t3)', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[
                          j.profession && `${j.profession}${j.specialty ? ` · ${j.specialty}` : ''}`,
                          (j.city || j.state) && [j.city, j.state].filter(Boolean).join(', '),
                          j.job_type,
                          j.client_name,
                        ].filter(Boolean).join('  ·  ')}
                      </td>
                      <td style={tdNum}>{j.submission_count ?? 0}</td>
                      <td style={tdNum}>{j.positions ?? 1}</td>
                      <td style={tdNum}>{j.age_days ?? 0}d</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Badge tone={STATUS_TONE[j.status]} style={{ textTransform: 'uppercase' }}>{j.status.replace('_', ' ')}</Badge>
                          {isOffPublicBoard(j) && (
                            <Badge tone="warn" title="Hidden from the public careers page and job feeds — add a location or pay rate to publish it.">
                              Not public
                            </Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </AuroraPage>
  );
}
